/**
 * 🎯 YETSAN Customer Intelligence Platform SDK
 * Insider-Style Queue-Based Analytics
 * 
 * Kullanım:
 * window.YetsanCIP = window.YetsanCIP || [];
 * YetsanCIP.push(['identify', { customer_id: 'M001', name: 'ABC Ltd' }]);
 * YetsanCIP.push(['track', 'add_to_cart', { sku: 'XYZ', qty: 2 }]);
 */

(function(window, document) {
  'use strict';

  // ==================== CONFIGURATION ====================
  const DEFAULT_CONFIG = {
    api_endpoint: process.env.REACT_APP_BACKEND_URL || '',
    batch_size: 20,
    // 15 sn: SDK içe aktarılınca bu varsayılanla başlar, CIPProvider'ın 10 sn ayarı zamanlayıcı kurulduktan
    // sonra geldiği için hiç uygulanmıyordu → fiilen 5 sn'de bir gönderiyordu (2026-10-23). Sekme gizlenince
    // ayrıca hemen gönderilir (visibility takibi), olay kaybı yok.
    batch_interval: 15000,
    heartbeat_interval: 60000,  // 60 saniye
    session_timeout: 1800000,   // 30 dakika
    storage_key: 'yetsan_cip',
    debug: true,                // DEBUG AÇIK
    auto_track: {
      page_views: true,
      clicks: true,
      forms: true,
      scroll: true,
      visibility: true,
      clipboard: true,          // 📋 Kopyala/Yapıştır tracking AÇIK
      errors: true
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const getTimestamp = () => new Date().toISOString();

  const log = (...args) => {
    if (CIP.config.debug) {
      console.log('[YetsanCIP]', ...args);
    }
  };

  const storage = {
    get: (key) => {
      try {
        const data = localStorage.getItem(`${DEFAULT_CONFIG.storage_key}_${key}`);
        return data ? JSON.parse(data) : null;
      } catch (e) { return null; }
    },
    set: (key, value) => {
      try {
        localStorage.setItem(`${DEFAULT_CONFIG.storage_key}_${key}`, JSON.stringify(value));
      } catch (e) {}
    },
    remove: (key) => {
      try {
        localStorage.removeItem(`${DEFAULT_CONFIG.storage_key}_${key}`);
      } catch (e) {}
    }
  };

  // ==================== DEVICE & CONTEXT ====================
  
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let deviceType = 'desktop';
    if (/Mobi|Android/i.test(ua)) deviceType = 'mobile';
    else if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';
    
    let browser = 'unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    
    let os = 'unknown';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'MacOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
    
    return {
      type: deviceType,
      browser,
      os,
      screen: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  };

  const getPageContext = () => ({
    url: window.location.pathname,
    full_url: window.location.href,
    title: document.title,
    referrer: document.referrer,
    query: window.location.search
  });

  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source'),
      medium: params.get('utm_medium'),
      campaign: params.get('utm_campaign'),
      term: params.get('utm_term'),
      content: params.get('utm_content')
    };
  };

  // ==================== MAIN CIP OBJECT ====================
  
  const CIP = {
    config: { ...DEFAULT_CONFIG },
    queue: [],
    eventBuffer: [],
    sentEventIds: new Set(),  // Gönderilmiş event_id'leri takip et
    session: null,
    profile: null,
    initialized: false,
    intervals: {},

    // ==================== INITIALIZATION ====================
    
    init() {
      if (this.initialized) return;
      
      log('🚀 Initializing...');
      
      // Session yönetimi
      this.initSession();
      
      // Auto tracking kurulumu
      this.setupAutoTracking();
      
      // Batch gönderimi başlat
      this.startBatchInterval();
      
      // Heartbeat başlat
      this.startHeartbeat();
      
      // Sayfa kapatma handler
      this.setupUnloadHandler();
      
      // Pending queue'yu işle
      this.processPendingQueue();
      
      this.initialized = true;
      log('✅ Initialized', { session: this.session?.id });
    },

    // ==================== SESSION MANAGEMENT ====================
    
    initSession() {
      // Mevcut session var mı kontrol et
      const existingSession = storage.get('session');
      const now = Date.now();
      
      if (existingSession && (now - existingSession.last_activity) < this.config.session_timeout) {
        // Session hala geçerli
        this.session = existingSession;
        this.session.last_activity = now;
        storage.set('session', this.session);
        log('📌 Existing session restored:', this.session.id);
      } else {
        // Yeni session oluştur
        this.session = {
          id: generateUUID(),
          started_at: getTimestamp(),
          last_activity: now,
          page_views: 0,
          events: 0,
          device: getDeviceInfo(),
          utm: getUTMParams(),
          referrer: document.referrer
        };
        storage.set('session', this.session);
        log('🆕 New session created:', this.session.id);
        
        // Session start event'i
        this._trackInternal('session_start', {
          device: this.session.device,
          utm: this.session.utm,
          referrer: this.session.referrer
        });
      }
      
      // Profile'ı yükle
      this.profile = storage.get('profile') || {};
    },

    updateSessionActivity() {
      if (this.session) {
        this.session.last_activity = Date.now();
        storage.set('session', this.session);
      }
    },

    // ==================== COMMAND PROCESSING ====================
    
    push(command) {
      if (!Array.isArray(command) || command.length === 0) return;
      
      const [method, ...args] = command;
      
      if (!this.initialized && method !== 'config') {
        // SDK henüz init olmadı, kuyruğa ekle
        this.queue.push(command);
        return;
      }
      
      // Komutu işle
      switch (method) {
        case 'config':
          this.configure(args[0]);
          break;
        case 'identify':
          this.identify(args[0]);
          break;
        case 'page':
          this.trackPage(args[0], args[1]);
          break;
        case 'track':
          this.track(args[0], args[1]);
          break;
        case 'segment':
          this.setSegment(args[0], args[1]);
          break;
        case 'tag':
          this.setTag(args[0], args[1]);
          break;
        case 'reset':
          this.reset();
          break;
        default:
          log('⚠️ Unknown command:', method);
      }
    },

    processPendingQueue() {
      const pending = [...this.queue];
      this.queue = [];
      pending.forEach(cmd => this.push(cmd));
    },

    // ==================== CONFIGURATION ====================
    
    configure(options) {
      // Auto_track'i ayrıca merge et (nested object)
      if (options.auto_track) {
        this.config.auto_track = { 
          ...this.config.auto_track, 
          ...options.auto_track 
        };
        delete options.auto_track;
      }
      
      this.config = { ...this.config, ...options };
      log('⚙️ Config updated:', this.config);
      
      // API endpoint güncellendiyse
      if (options.api_endpoint) {
        log('📡 API endpoint:', options.api_endpoint);
      }
      
      // Init edilmemişse init et
      if (!this.initialized) {
        this.init();
      }
    },

    // ==================== IDENTIFY ====================
    
    identify(attributes) {
      if (!attributes) return;
      
      // Profile'ı güncelle
      this.profile = {
        ...this.profile,
        ...attributes,
        identified_at: getTimestamp()
      };
      storage.set('profile', this.profile);
      
      log('👤 Identified:', this.profile.customer_id || this.profile.email);
      
      // Identify event'i gönder
      this._trackInternal('identify', {
        customer_id: this.profile.customer_id,
        attributes: attributes
      });
    },

    // ==================== PAGE TRACKING ====================
    
    trackPage(pageType, attributes = {}) {
      this.session.page_views++;
      this.updateSessionActivity();
      
      const pageData = {
        page_type: pageType || 'other',
        ...getPageContext(),
        ...attributes
      };
      
      log('📄 Page:', pageType, attributes);
      this._trackInternal('page_view', pageData);
    },

    // ==================== EVENT TRACKING ====================
    
    track(eventName, data = {}) {
      this.session.events++;
      this.updateSessionActivity();
      
      log('📊 Track:', eventName, data);
      this._trackInternal(eventName, data);
    },

    _trackInternal(eventType, data) {
      const event = {
        event_id: generateUUID(),
        type: eventType,
        timestamp: getTimestamp(),
        session_id: this.session?.id,
        customer_id: this.profile?.customer_id,
        customer_name: this.profile?.name || this.profile?.cari_unvan,
        page: getPageContext(),
        data: data
      };
      
      this.eventBuffer.push(event);
      
      // Buffer doluysa hemen gönder
      if (this.eventBuffer.length >= this.config.batch_size) {
        this.flush();
      }
    },

    // ==================== SEGMENT & TAGS ====================
    
    setSegment(segmentId, value = true) {
      if (!this.profile.segments) this.profile.segments = {};
      this.profile.segments[segmentId] = value;
      storage.set('profile', this.profile);
      
      this._trackInternal('segment_update', { segment_id: segmentId, value });
    },

    setTag(tag, value = true) {
      if (!this.profile.tags) this.profile.tags = {};
      this.profile.tags[tag] = value;
      storage.set('profile', this.profile);
    },

    // ==================== AUTO TRACKING ====================
    
    setupAutoTracking() {
      const auto = this.config.auto_track;
      
      // Click tracking
      if (auto.clicks) {
        document.addEventListener('click', (e) => {
          const target = e.target.closest('[data-cip-track], [data-testid], button, a');
          if (!target) return;
          
          this._trackInternal('click', {
            element: target.tagName.toLowerCase(),
            text: target.innerText?.substring(0, 50),
            cip_track: target.dataset?.cipTrack,
            testid: target.dataset?.testid,
            href: target.href,
            id: target.id,
            class: target.className
          });
        }, { passive: true });
      }
      
      // Scroll tracking
      if (auto.scroll) {
        let maxDepth = 0;
        let scrollTimeout;
        
        window.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const depth = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
            
            if (depth > maxDepth && depth % 25 === 0) {
              maxDepth = depth;
              this._trackInternal('scroll_depth', { depth });
            }
          }, 100);
        }, { passive: true });
      }
      
      // Visibility tracking
      if (auto.visibility) {
        let hiddenTime = null;
        
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            hiddenTime = Date.now();
            this._trackInternal('tab_hidden', {
              page: window.location.pathname,
              last_product: storage.get('last_product'),
              last_search: storage.get('last_search'),
              last_copy: storage.get('last_copy')
            });
            // Hemen flush et
            this.flush();
          } else if (hiddenTime) {
            const awayDuration = Date.now() - hiddenTime;
            this._trackInternal('tab_visible', {
              away_duration_ms: awayDuration,
              away_seconds: Math.round(awayDuration / 1000)
            });
            hiddenTime = null;
          }
        });
      }
      
      // Clipboard tracking
      if (auto.clipboard) {
        document.addEventListener('copy', () => {
          const text = window.getSelection()?.toString()?.trim();
          if (text && text.length >= 3 && text.length <= 100) {
            const type = this.detectContentType(text);
            storage.set('last_copy', { content: text, type, at: Date.now() });
            this._trackInternal('copy', { content: text.substring(0, 50), type });
          }
        });
        
        document.addEventListener('paste', (e) => {
          // Tüm paste'leri yakala (sadece input değil)
          const text = e.clipboardData?.getData('text')?.trim();
          if (text && text.length >= 3 && text.length <= 100) {
            const type = this.detectContentType(text);
            const target = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' 
              ? (e.target.name || e.target.placeholder || e.target.className || 'input')
              : (e.target.className || e.target.id || 'page');
            this._trackInternal('paste', { 
              content: text.substring(0, 50), 
              type,
              target: target
            });
          }
        });
      }
      
      // Error tracking
      if (auto.errors) {
        window.addEventListener('error', (e) => {
          this._trackInternal('error', {
            message: e.message,
            filename: e.filename,
            line: e.lineno,
            column: e.colno
          });
        });
      }
      
      // Form tracking
      if (auto.forms) {
        document.addEventListener('submit', (e) => {
          if (e.target.tagName === 'FORM') {
            this._trackInternal('form_submit', {
              form_id: e.target.id,
              form_name: e.target.name,
              action: e.target.action
            });
          }
        });
      }
    },

    detectContentType(text) {
      if (/^[A-Z0-9\-]{4,20}$/i.test(text)) return 'sku';
      if (/^[A-Z0-9\s\-\.]{6,30}$/i.test(text)) return 'oem';
      if (/^\d+([.,]\d{1,2})?$/.test(text)) return 'price';
      return 'text';
    },

    // ==================== BATCH SENDING ====================
    
    startBatchInterval() {
      if (this.intervals.batch) return;
      
      this.intervals.batch = setInterval(() => {
        this.flush();
      }, this.config.batch_interval);
    },

    async flush() {
      // Susturulmuş (yalnız MES'e giren hesap: müşteri zekâsı ucu ona kapalı) → biriken olaylar atılır, istek gitmez
      if (this.susturuldu) { this.eventBuffer = []; return; }
      if (this.eventBuffer.length === 0) return;
      
      // Daha önce gönderilmemiş event'leri filtrele
      const newEvents = this.eventBuffer.filter(e => !this.sentEventIds.has(e.event_id));
      this.eventBuffer = [];
      
      if (newEvents.length === 0) {
        log('⏭️ No new events to flush');
        return;
      }
      
      log(`📤 Flushing ${newEvents.length} events...`);
      log('📤 Endpoint:', `${this.config.api_endpoint}/api/cip/ingest/batch`);
      log('📤 Token:', this.getAuthToken() ? 'Present' : 'Missing');
      
      try {
        const response = await fetch(`${this.config.api_endpoint}/api/cip/ingest/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': this.getAuthToken()
          },
          body: JSON.stringify({
            session_id: this.session?.id,
            customer_id: this.profile?.customer_id,
            events: newEvents
          })
        });
        
        log('📤 Response status:', response.status);
        
        const result = await response.json();
        
        if (result.success) {
          // Gönderilen event_id'leri kaydet
          newEvents.forEach(e => this.sentEventIds.add(e.event_id));
          // Set'i temiz tut (max 1000 event_id)
          if (this.sentEventIds.size > 1000) {
            const arr = Array.from(this.sentEventIds);
            this.sentEventIds = new Set(arr.slice(-500));
          }
          log(`✅ Sent ${result.processed} events`);
        } else {
          // Başarısızsa buffer'a geri ekle (gönderilmemişleri)
          this.eventBuffer = [...newEvents, ...this.eventBuffer];
          log('⚠️ Send failed:', result.error || 'Unknown error');
        }
      } catch (err) {
        // Hata durumunda buffer'a geri ekle
        this.eventBuffer = [...newEvents, ...this.eventBuffer];
        log('❌ Send error:', err.message);
      }
    },

    // ==================== HEARTBEAT ====================
    
    startHeartbeat() {
      if (this.intervals.heartbeat) return;
      
      this.intervals.heartbeat = setInterval(() => {
        this.sendHeartbeat();
      }, this.config.heartbeat_interval);
    },

    async sendHeartbeat() {
      if (!this.session || this.susturuldu) return;
      
      try {
        await fetch(`${this.config.api_endpoint}/api/cip/heartbeat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': this.getAuthToken()
          },
          body: JSON.stringify({
            session_id: this.session.id,
            customer_id: this.profile?.customer_id,
            page: window.location.pathname,
            status: document.hidden ? 'idle' : 'active'
          })
        });
        log('💓 Heartbeat sent');
      } catch (err) {
        log('❌ Heartbeat error:', err.message);
      }
    },

    // ==================== UNLOAD HANDLER ====================
    
    setupUnloadHandler() {
      window.addEventListener('beforeunload', () => {
        // Session'ı kaydet
        if (this.session) {
          this.session.ended_at = getTimestamp();
          storage.set('session', this.session);
        }
        
        if (this.susturuldu) return;
        // Kalan event'leri sendBeacon ile gönder
        if (this.eventBuffer.length > 0) {
          navigator.sendBeacon(
            `${this.config.api_endpoint}/api/cip/ingest/beacon`,
            JSON.stringify({
              session_id: this.session?.id,
              customer_id: this.profile?.customer_id,
              events: this.eventBuffer
            })
          );
        }
        
        // Session end
        navigator.sendBeacon(
          `${this.config.api_endpoint}/api/cip/session/end`,
          JSON.stringify({
            session_id: this.session?.id,
            reason: 'page_close'
          })
        );
      });
    },

    // ==================== HELPERS ====================
    
    getAuthToken() {
      // localStorage'dan token al
      try {
        // Önce 'token' key'ini dene (AuthContext bu şekilde saklıyor)
        const token = localStorage.getItem('token');
        if (token) {
          return `Bearer ${token}`;
        }
        // Fallback: 'auth' objesinden dene
        const authData = localStorage.getItem('auth');
        if (authData) {
          const parsed = JSON.parse(authData);
          return parsed.token ? `Bearer ${parsed.token}` : '';
        }
      } catch (e) {}
      return '';
    },

    // ==================== PUBLIC HELPERS ====================
    
    // Ürün görüntüleme (shorthand)
    trackProduct(sku, name, price, data = {}) {
      storage.set('last_product', { sku, name, price, at: Date.now() });
      this.track('product_view', { sku, name, price, ...data });
    },
    
    // Arama (shorthand) - Duplicate prevention
    trackSearch(query, resultCount, results = []) {
      // Aynı aramayı 2 saniye içinde tekrar gönderme
      const lastSearch = storage.get('last_search');
      if (lastSearch && lastSearch.query === query && (Date.now() - lastSearch.at) < 2000) {
        log('⏭️ Duplicate search skipped:', query);
        return;
      }
      
      storage.set('last_search', { query, result_count: resultCount, at: Date.now() });
      this.track('search', { 
        query, 
        result_count: resultCount,
        top_results: results.slice(0, 5).map(r => r.sku || r)
      });
    },
    
    // Sepete ekleme (shorthand)
    trackCartAdd(sku, name, quantity, price) {
      this.track('cart_add', { sku, name, quantity, price, total: quantity * price });
    },
    
    // Sepetten çıkarma (shorthand)
    trackCartRemove(sku, quantity) {
      this.track('cart_remove', { sku, quantity });
    },
    
    // Satın alma (shorthand)
    trackPurchase(orderId, totalValue, items = []) {
      this.track('purchase', { 
        order_id: orderId, 
        total_value: totalValue,
        item_count: items.length,
        items: items.slice(0, 10)
      });
    },

    // ==================== RESET ====================
    
    reset() {
      // Tüm verileri temizle
      storage.remove('session');
      storage.remove('profile');
      storage.remove('last_product');
      storage.remove('last_search');
      storage.remove('last_copy');
      
      this.session = null;
      this.profile = {};
      this.eventBuffer = [];
      
      // Interval'ları temizle
      Object.values(this.intervals).forEach(clearInterval);
      this.intervals = {};
      
      this.initialized = false;
      log('🔄 Reset complete');
    },

    // ==================== DEBUG ====================
    
    getState() {
      return {
        initialized: this.initialized,
        session: this.session,
        profile: this.profile,
        eventBuffer: this.eventBuffer.length,
        config: this.config
      };
    }
  };

  // ==================== GLOBAL SETUP ====================
  
  // Mevcut queue'yu işle
  const existingQueue = window.YetsanCIP || [];
  
  // Global objeyi oluştur
  window.YetsanCIP = {
    push: (cmd) => CIP.push(cmd),
    identify: (attrs) => CIP.identify(attrs),
    track: (name, data) => CIP.track(name, data),
    trackProduct: (sku, name, price, data) => CIP.trackProduct(sku, name, price, data),
    trackSearch: (query, count, results) => CIP.trackSearch(query, count, results),
    trackCartAdd: (sku, name, qty, price) => CIP.trackCartAdd(sku, name, qty, price),
    trackCartRemove: (sku, qty) => CIP.trackCartRemove(sku, qty),
    trackPurchase: (id, value, items) => CIP.trackPurchase(id, value, items),
    page: (type, attrs) => CIP.trackPage(type, attrs),
    reset: () => CIP.reset(),
    debug: () => CIP.getState(),
    flush: () => CIP.flush(),
    sustur: (v) => { CIP.susturuldu = !!v; if (v) CIP.eventBuffer = []; }
  };

  // Pending komutları işle
  if (Array.isArray(existingQueue)) {
    existingQueue.forEach(cmd => CIP.push(cmd));
  }

  // Auto-init with default config
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CIP.init());
  } else {
    CIP.init();
  }

  log('📦 SDK loaded');

})(window, document);
